/**
 * Simplified Product Service
 * Clean API calls with CompanyID headers and proper image expansion
 */

import { apiService, imageHelpers, tokenManager } from "./api.js";
import useSpecialPricingStore from "../store/specialPricingStore.js";

class ProductService {
  /**
   * Get current company ID for filtering
   */
  _getCurrentCompanyId() {
    const currentCompany = tokenManager.getCurrentCompany();
    return currentCompany?.id || null;
  }

  /**
   * Build base filters for all product queries
   */
  _buildBaseFilters() {
    const companyId = this._getCurrentCompanyId();
    const filters = ["OptStatusID eq 1"]; // Only active products

    if (companyId) {
      filters.push(`CompanyID eq ${companyId}`);
    }

    return filters;
  }

  /**
   * Get categories that have products
   * Uses: GET /storefront/categories (shop-owned, self-scoped)
   */
  async getCategories() {
    try {
      // Shop-owned, self-scoped catalog read (D2). Server returns the caller's company's active
      // categories that have products, already ordered; slug fallback stays client-side.
      const categories = await apiService.get("/storefront/categories");
      return (categories || []).map((c) => ({
        ...c,
        slug: c.slug || this._createSlug(c.name),
      }));
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
  }

  /**
   * Get brands that have products
   * Uses: GET /storefront/brands (shop-owned, self-scoped)
   */
  async getBrands() {
    try {
      // Shop-owned, self-scoped catalog read (D2). Server returns the caller's company's active
      // brands that have products, already ordered; slug fallback stays client-side.
      const brands = await apiService.get("/storefront/brands");
      return (brands || []).map((b) => ({
        ...b,
        description: b.description || "",
        slug: b.slug || this._createSlug(b.name),
      }));
    } catch (error) {
      console.error("Error fetching brands:", error);
      return [];
    }
  }

  /**
   * Get featured brands for carousel
   * Returns only brands where isFeatured = true
   */
  async getFeaturedBrands() {
    const brands = await this.getBrands();
    return brands.filter((brand) => brand.isFeatured);
  }

  /**
   * Get products with filtering and pagination - UPDATED WITH PROPER EXPANSION
   * Uses: /odata/ProductSKUVariantsWithTotals with expand for images
   */
  async getProducts(options = {}) {
    try {
      const {
        page = 1,
        pageSize = 24,
        categoryId,
        brandId,
        search,
        sortBy = "ProductName",
        sortDirection = "asc",
        inStock = false,
        onSale = false,
        backInStock = false,
        isMed = null,      // Med/Rec filter: true = medical only
        isRec = null,      // Med/Rec filter: true = recreational only
      } = options;

      // Build filters
      const filters = this._buildBaseFilters();

      if (inStock) {
        filters.push("(unitAvailable gt 0 or allowBackorder eq true)");
      }

      if (onSale) {
        filters.push("discountUnitPrice ne null");
      }

      if (backInStock) {
        // Back in stock: product was out of stock and recently got restocked
        // BackInStockDate is stamped by the backend when a lot is added to an out-of-stock product
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        const dateFilter = fourteenDaysAgo.toISOString();

        filters.push(`backInStockDate ge ${dateFilter}`);
        filters.push("(unitAvailable gt 0 or allowBackorder eq true)");
      }

      if (categoryId) {
        filters.push(`productSectionID eq ${categoryId}`);
      }

      if (brandId) {
        filters.push(`brandID eq ${brandId}`);
      }

      // Med/Rec filtering
      if (isMed === true) {
        filters.push(`isMed eq true`);
      }
      if (isRec === true) {
        filters.push(`isRec eq true`);
      }

      if (search) {
        const searchFilter = [
          `contains(tolower(productName), '${search.toLowerCase()}')`,
          `contains(tolower(flavor), '${search.toLowerCase()}')`,
          `contains(tolower(brandName), '${search.toLowerCase()}')`,
          `contains(tolower(productSectionName), '${search.toLowerCase()}')`,
        ].join(" or ");
        filters.push(`(${searchFilter})`);
      }

      // Map friendly sort names to OData field names
      const sortFieldMap = {
        'name': 'productName',
        'ProductName': 'productName',
        'newest': 'dateCreated',
        'price': 'productName', // price sort on nested entity not supported, fallback to name
        'popular': 'productName',
      };

      // Resolve each field in sortBy (supports comma-separated, e.g. "brandDisplayOrder, productName")
      const resolvedSortBy = sortBy.split(',')
        .map(f => sortFieldMap[f.trim()] || f.trim())
        .join(', ');

      // Build $orderby
      const orderby = resolvedSortBy.split(',')
        .map(f => `${f.trim()} ${sortDirection}`)
        .join(', ');

      const queryOptions = {
        $filter: filters.join(" and "),
        $expand:
          "productSKUVariant($expand=productSKUVariantImages($expand=image),ProductSkuVariantMOQPricing)",
        $orderby: orderby,
        $top: pageSize,
        $skip: (page - 1) * pageSize,
        $count: true,
      };

      const response = await apiService.get(
        "/storefront-odata/vwStorefrontProductsWithTotals",
        queryOptions,
      );

      // Ensure the customer special-pricing overlay is loaded before transforming,
      // so the calculator resolves the correct price on first render.
      await useSpecialPricingStore.getState().ensureLoaded();

      const products = (response.value || []).map((product) =>
        this._transformProduct(product)
      );

      return {
        products,
        pagination: {
          page,
          pageSize,
          totalItems: response["@odata.count"] || 0,
          totalPages: Math.ceil((response["@odata.count"] || 0) / pageSize),
        },
      };
    } catch (error) {
      console.error("Error in productService.getProducts:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        response: error.response,
      });
      throw error;
    }
  }

  /**
   * Get single product details - UPDATED WITH BATCH SUPPORT
   * Uses: /odata/ProductSKUVariantsWithTotals([ID]) with full expansion
   */
  async getProduct(productId) {
    try {
      // Expand images, batch lots (for COA/test results), and MOQ pricing
      const response = await apiService.get(
        `/storefront-odata/vwStorefrontProductsWithTotals(${productId})`,
        {
          $expand:
            "ProductSKUVariant($expand=ProductSKUVariantImages($expand=Image;$orderby=listOrder asc),ProductBatchLots($expand=ProductBatch($expand=ProductBatchFiles)),ProductSkuVariantMOQPricing),Brand,ProductSection",
        },
      );

      // Check if product is active
      if (response.optStatusID !== 1) {
        const error = new Error("Product not found or inactive");
        error.status = 404;
        error.isProductNotFound = true;
        throw error;
      }

      await useSpecialPricingStore.getState().ensureLoaded();
      return this._transformFullProductWithBatches(response);
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);

      if (error.status === 404 || error.isProductNotFound) {
        throw error;
      }

      throw error;
    }
  }

  // ADD this new transformation method to productService.js:

  /**
   * Transform full product from ProductSKUVariantsWithTotals with batches
   * REPLACES the existing _transformFullProduct method
   */
  _transformFullProductWithBatches(apiProduct) {
    const images = this._getProductImages(apiProduct);
    const batches = this._transformProductBatchLots(
      apiProduct.productSKUVariant?.productBatchLots || [],
    );

    return {
      // Basic product info
      id: apiProduct.productSkuVariantID,
      name: apiProduct.productName,
      flavor: apiProduct.flavor || "",
      description: apiProduct.description || "",
      legacySKU: apiProduct.legacySKU || "",

      // Pricing and availability - read directly from view
      unitPrice: apiProduct.unitPrice || 0,
      discountUnitPrice: apiProduct.discountUnitPrice || null,
      unitsPerCase: apiProduct.unitsPerCase || 1,
      singleUnitFee: parseFloat(apiProduct.singleUnitFee || 0),
      optSellByTypeID: apiProduct.optSellByTypeID || 1,

      // MOQ/Volume pricing tiers (still from expand)
      productSkuVariantMOQPricing: apiProduct.productSKUVariant?.productSkuVariantMOQPricing || [],

      // Stock information
      unitAvailable: apiProduct.unitAvailable || 0,
      isInStock: (apiProduct.unitAvailable || 0) > 0,
      allowBackorder: apiProduct.allowBackorder || false,

      // Physical properties - read directly from view
      weight: apiProduct.weight || 0,

      // Brand info
      brand: apiProduct.brand
        ? {
            id: apiProduct.brand.id,
            name: apiProduct.brand.name,
            slug:
              apiProduct.brand.slug || this._createSlug(apiProduct.brand.name),
            description: apiProduct.brand.description || "",
            url: apiProduct.brand.url,
            imageUrl: imageHelpers.getBrandImageUrl(apiProduct.brand),
            isHouseBrand: apiProduct.brand.isHouseBrand || false,
          }
        : null,

      // Category info
      category: apiProduct.productSection
        ? {
            id: apiProduct.productSection.id,
            name: apiProduct.productSection.name,
            slug:
              apiProduct.productSection.slug ||
              this._createSlug(apiProduct.productSection.name),
            color: apiProduct.productSection.color,
            iconLight: apiProduct.productSection.icon_Light,
            iconDark: apiProduct.productSection.icon_Dark,
          }
        : null,

      // Images
      images: images,
      primaryImage: images.find((img) => img.isPrimary) || images[0] || null,
      imageUrl:
        images.length > 0
          ? images[0].fullPath
          : this._getProductImageUrl(apiProduct),
      hasImages: images.length > 0,

      // NEW: Batch information
      batches: batches,
      batchCount: apiProduct.batchCount || 0,
      hasBatches: batches.length > 0,

      // Dates and status
      dateCreated: apiProduct.dateCreated,
      optStatusID: apiProduct.optStatusID,

      // Med/Rec status - read directly from view
      isMed: apiProduct.isMed || false,
      isRec: apiProduct.isRec || false,

      // Custom case/unit labels - read directly from view
      caseLabel: apiProduct.caseLabel || null,
      unitLabel: apiProduct.unitLabel || null,
    };
  }

  /**
   * Get product image URL from expanded data
   * @private
   */
  _getProductImageUrl(product) {
    // Check for expanded ProductSkuVariant images first (lowercase 'productSKUVariant')
    if (product.productSKUVariant?.productSKUVariantImages?.length > 0) {
      const firstImage = product.productSKUVariant.productSKUVariantImages[0];
      if (firstImage.image?.fullPath) {
        return `https://stashhouseimages.blob.core.windows.net/${firstImage.image.fullPath}`;
      }
    }

    // Check for direct ProductSkuVariantImages (for single product calls)
    if (product.ProductSkuVariantImages?.length > 0) {
      const firstImage = product.ProductSkuVariantImages[0];
      if (firstImage.Image?.FullPath) {
        return `https://stashhouseimages.blob.core.windows.net/${firstImage.Image.FullPath}`;
      }
    }

    // Fallback to legacy fields
    if (product.productImageURL) {
      return imageHelpers.getImageUrl(product.productImageURL);
    }

    if (product.ProductImageURL) {
      return imageHelpers.getImageUrl(product.ProductImageURL);
    }

    return "/placeholder-product.jpg";
  }

  /**
   * Get all product images from expanded data
   * @private
   */
  _getProductImages(product) {
    const images = [];

    // Check for expanded productSKUVariant images (lowercase 'productSKUVariant')
    const imageSource =
      product.productSKUVariant?.productSKUVariantImages ||
      product.ProductSkuVariantImages ||
      [];

    imageSource.forEach((imageRelation) => {
      if (imageRelation.image?.fullPath) {
        images.push({
          id: imageRelation.image.id,
          fullPath: `https://stashhouseimages.blob.core.windows.net/${imageRelation.image.fullPath}`,
          displayOrder: imageRelation.listOrder || 0,
          isPrimary: imageRelation.image.isPrimary || false,
        });
      }
    });

    // Sort by display order, then by primary
    images.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return a.displayOrder - b.displayOrder;
    });

    return images;
  }

  /**
   * Transform product from ProductSKUVariantsWithTotals view - UPDATED FOR YOUR API STRUCTURE
   */
  _transformProduct(apiProduct) {
    const images = this._getProductImages(apiProduct);
    const variant = apiProduct.productSKUVariant;

    return {
      id: apiProduct.productSkuVariantID,
      name: apiProduct.productName,
      flavor: apiProduct.flavor || "",
      brandId: apiProduct.brandID,
      brandName: apiProduct.brandName || "",
      categoryId: apiProduct.productSectionID,
      categoryName: apiProduct.productSectionName || "",
      unitAvailable: apiProduct.unitAvailable || 0,
      isInStock: (apiProduct.unitAvailable || 0) > 0,
      allowBackorder: apiProduct.allowBackorder || false,

      // Image handling - use expanded images or fallback
      imageUrl:
        images.length > 0
          ? images[0].fullPath
          : this._getProductImageUrl(apiProduct),
      images: images,
      hasImages: images.length > 0,

      // Product details - read directly from view
      unitPrice: apiProduct.unitPrice || 0,
      discountUnitPrice: apiProduct.discountUnitPrice || null,
      unitsPerCase: apiProduct.unitsPerCase || 1,
      optSellByTypeID: apiProduct.optSellByTypeID || 1,
      description: apiProduct.description || "",
      weight: apiProduct.weight || 0,
      singleUnitFee: apiProduct.singleUnitFee || 0,

      // MOQ/Volume pricing tiers (still from expand)
      productSkuVariantMOQPricing: variant?.productSkuVariantMOQPricing || [],

      // Med/Rec status - read directly from view
      isMed: apiProduct.isMed || false,
      isRec: apiProduct.isRec || false,

      // Custom case/unit labels - read directly from view
      caseLabel: apiProduct.caseLabel || null,
      unitLabel: apiProduct.unitLabel || null,
    };
  }

  /**
   * Transform full product from ProductSKUVariants table - UPDATED FOR IMAGES
   */
  _transformFullProduct(apiProduct) {
    const images = this._getProductImages(apiProduct);

    return {
      id: apiProduct.ID,
      name: apiProduct.Name,
      flavor: apiProduct.Flavor || "",
      description: apiProduct.Description || "",
      unitPrice: apiProduct.UnitPrice || 0,
      price: apiProduct.Price || 0,
      unitsPerCase: apiProduct.UnitsPerCase || 1,
      optSellByTypeID: apiProduct.optSellByTypeID || false,
      unitAvailable: apiProduct.UnitAvailable || 0,
      isInStock: (apiProduct.UnitAvailable || 0) > 0,
      allowBackorder: apiProduct.AllowBackorder || false,
      weight: apiProduct.Weight || 0,

      // Brand info
      brand: apiProduct.ProductSKU?.Brand
        ? {
            id: apiProduct.ProductSKU.Brand.ID,
            name: apiProduct.ProductSKU.Brand.Name,
            slug:
              apiProduct.ProductSKU.Brand.Slug ||
              this._createSlug(apiProduct.ProductSKU.Brand.Name),
          }
        : null,

      // Category info
      category: apiProduct.ProductSection
        ? {
            id: apiProduct.ProductSection.ID,
            name: apiProduct.ProductSection.Name,
            slug:
              apiProduct.ProductSection.Slug ||
              this._createSlug(apiProduct.ProductSection.Name),
          }
        : null,

      // Image handling - use expanded images
      imageUrl:
        images.length > 0
          ? images[0].fullPath
          : this._getProductImageUrl(apiProduct),
      images: images,
      hasImages: images.length > 0,
      primaryImage: images.find((img) => img.isPrimary) || images[0] || null,
    };
  }

  /**
   * Transform product batch lots with nested batch and testing files
   * @private
   */
  _transformProductBatchLots(apiBatchLots) {
    if (!Array.isArray(apiBatchLots)) return [];

    // Group batch lots by their parent batch to avoid duplicate batch entries
    const batchMap = new Map();

    apiBatchLots
      .filter((lot) => {
        const batch = lot.productBatch;
        return (
          lot.optStatusID === 1 &&
          lot.available > 0 &&
          lot.expirationDate != null && new Date(lot.expirationDate) >= new Date() &&
          lot.productBatchID != null &&
          batch?.optBatchTestingStatusID === 3
        );
      })
      .forEach((lot) => {
        const batch = lot.productBatch;
        if (!batch) return;

        if (!batchMap.has(batch.id)) {
          batchMap.set(batch.id, {
            id: batch.id,
            batchName: batch.batchName,
            dateCreated: batch.dateCreated,
            testingStatusID: batch.optBatchTestingStatusID,
            lots: [],
            testingFiles: (batch.productBatchFiles || []).map((file) => ({
              id: file.id,
              fileName: file.coa || "Testing Results",
              fileUrl: file.coa
                ? `https://stashhouseimages.blob.core.windows.net/productbatchfiles/${file.coa}`
                : null,
              dateCreated: file.dateCreated,
              isPDF: true,
              displayName: file.coa || `${batch.batchName} - Testing Results`,
            })),
          });
        }

        // Add lot info to the batch
        batchMap.get(batch.id).lots.push({
          id: lot.id,
          rfid: lot.rfid,
          available: lot.available,
          onHand: lot.onHand,
          committed: lot.committed,
          picked: lot.picked,
        });
      });

    // Convert map to array and sort by date
    return Array.from(batchMap.values())
      .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
  }
  /**
   * Get new arrivals - products created within the last 30 days, in stock only
   * Uses: /odata/ProductSKUVariantsWithTotals with dateCreated filter
   */
  async getNewArrivals(options = {}) {
    try {
      const {
        page = 1,
        pageSize = 24,
        sortBy = "dateCreated",
        sortDirection = "desc",
        isMed = null,
        isRec = null,
      } = options;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateFilter = thirtyDaysAgo.toISOString();

      // Build filters
      const filters = this._buildBaseFilters();
      filters.push(`dateCreated ge ${dateFilter}`);
      filters.push("(unitAvailable gt 0 or allowBackorder eq true)");

      // Med/Rec filtering
      if (isMed === true) {
        filters.push(`isMed eq true`);
      }
      if (isRec === true) {
        filters.push(`isRec eq true`);
      }

      const queryOptions = {
        $filter: filters.join(" and "),
        $expand:
          "productSKUVariant($expand=productSKUVariantImages($expand=image),ProductSkuVariantMOQPricing)",
        $orderby: `${sortBy} ${sortDirection}`,
        $top: pageSize,
        $skip: (page - 1) * pageSize,
        $count: true,
      };

      const response = await apiService.get(
        "/storefront-odata/vwStorefrontProductsWithTotals",
        queryOptions,
      );

      // Ensure the customer special-pricing overlay is loaded before transforming,
      // so the calculator resolves the correct price on first render.
      await useSpecialPricingStore.getState().ensureLoaded();

      const products = (response.value || []).map((product) =>
        this._transformProduct(product)
      );

      return {
        products,
        pagination: {
          page,
          pageSize,
          totalItems: response["@odata.count"] || 0,
          totalPages: Math.ceil((response["@odata.count"] || 0) / pageSize),
        },
      };
    } catch (error) {
      console.error("Error in productService.getNewArrivals:", error);
      throw error;
    }
  }

  /**
   * Search products with expanded images
   */
  async searchProducts(query, options = {}) {
    if (!query || query.trim().length < 2) {
      return {
        products: [],
        pagination: { page: 1, pageSize: 0, totalItems: 0, totalPages: 0 },
      };
    }

    return this.getProducts({
      search: query.trim(),
      ...options,
    });
  }

  /**
   * Get products by category with expanded images
   */
  async getProductsByCategory(categoryId, options = {}) {
    return this.getProducts({
      categoryId,
      ...options,
    });
  }

  /**
   * Get products by brand with expanded images
   */
  async getProductsByBrand(brandId, options = {}) {
    return this.getProducts({
      brandId,
      ...options,
    });
  }

  /**
   * Get featured products
   */
  async getFeaturedProducts(options = {}) {
    // You might have a featured flag or category
    return this.getProducts({
      // featured: true, // if you have this field
      sortBy: "DisplayOrder",
      sortDirection: "asc",
      pageSize: 12,
      ...options,
    });
  }

  /**
   * Create URL-friendly slug from name
   */
  _createSlug(name) {
    if (!name) return "";
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  /**
   * Get lab report brands with COA counts using SQL view
   * Uses vwLabReportBrands view for efficient single query
   */
  async getLabReportBrands() {
    try {
      const response = await apiService.get("/storefront-odata/vwLabReportBrands", {
        $orderby: "BrandName asc",
      });

      const brands = (response.value || []).map((brand) => ({
        id: brand.brandID,
        name: brand.brandName || "Unknown Brand",
        slug: brand.brandSlug || this._createSlug(brand.brandName || "unknown"),
        imageUrl: brand.brandImageURL || null,
        productCount: brand.productCount || 0,
        batchCount: brand.batchCount || 0,
      }));

      console.log(`Lab reports: found ${brands.length} brands with COAs`);
      return brands;
    } catch (error) {
      console.error("Error in productService.getLabReportBrands:", error);
      throw error;
    }
  }

  /**
   * Get lab report products for a specific brand using SQL view
   * Uses vwLabReportProducts view with $filter for efficient query
   * Returns products grouped with their COA files
   */
  async getLabReportProductsByBrand(brandId) {
    try {
      const response = await apiService.get("/storefront-odata/vwLabReportProducts", {
        $filter: `BrandID eq ${brandId}`,
        $orderby: "ProductName asc, BatchDateCreated desc",
      });

      // Group by product and batch since view returns one row per COA file
      const productMap = new Map();
      const seenCOAs = new Set(); // Track seen COA file IDs to prevent duplicates

      (response.value || []).forEach((row) => {
        const productId = row.productSkuVariantID;
        const batchId = row.productBatchID;
        const coaFileId = row.productBatchFileID;

        // Skip if we've already processed this COA file
        if (seenCOAs.has(coaFileId)) {
          return;
        }
        seenCOAs.add(coaFileId);

        if (!productMap.has(productId)) {
          productMap.set(productId, {
            id: productId,
            name: row.productName || "",
            flavor: row.flavor || "",
            batchMap: new Map(), // Use a Map for batches too
          });
        }

        const product = productMap.get(productId);

        if (!product.batchMap.has(batchId)) {
          product.batchMap.set(batchId, {
            id: batchId,
            batchName: row.batchName || "",
            dateCreated: row.batchDateCreated,
            testingFiles: [],
          });
        }

        // Add COA to batch
        if (row.coa) {
          product.batchMap.get(batchId).testingFiles.push({
            id: coaFileId,
            fileName: row.coa,
            fileUrl: `https://stashhouseimages.blob.core.windows.net/productbatchfiles/${row.coa}`,
            dateCreated: row.batchDateCreated,
          });
        }
      });

      // Convert Maps to arrays and add batch counts
      const products = Array.from(productMap.values()).map((product) => {
        const batches = Array.from(product.batchMap.values());
        return {
          id: product.id,
          name: product.name,
          flavor: product.flavor,
          batches: batches,
          batchCount: batches.length,
        };
      });

      return products;
    } catch (error) {
      console.error(
        "Error in productService.getLabReportProductsByBrand:",
        error
      );
      throw error;
    }
  }

  /**
   * Search lab report products by product name, flavor, or batch name
   * Returns results grouped by brand -> product -> batch
   */
  async searchLabReports(searchTerm) {
    try {
      const search = searchTerm.toLowerCase();

      // Build OData filter for searching
      const filters = [
        `contains(tolower(ProductName), '${search}')`,
        `contains(tolower(Flavor), '${search}')`,
        `contains(tolower(BatchName), '${search}')`,
      ];

      const response = await apiService.get("/storefront-odata/vwLabReportProducts", {
        $filter: `(${filters.join(" or ")})`,
        $orderby: "ProductName asc, BatchDateCreated desc",
      });

      // Group by brand, then product, then batch
      const brandMap = new Map();
      const seenCOAs = new Set();

      (response.value || []).forEach((row) => {
        const brandId = row.brandID;
        const productId = row.productSkuVariantID;
        const batchId = row.productBatchID;
        const coaFileId = row.productBatchFileID;

        if (seenCOAs.has(coaFileId)) return;
        seenCOAs.add(coaFileId);

        if (!brandMap.has(brandId)) {
          brandMap.set(brandId, {
            id: brandId,
            productMap: new Map(),
          });
        }

        const brand = brandMap.get(brandId);

        if (!brand.productMap.has(productId)) {
          brand.productMap.set(productId, {
            id: productId,
            name: row.productName || "",
            flavor: row.flavor || "",
            batchMap: new Map(),
          });
        }

        const product = brand.productMap.get(productId);

        if (!product.batchMap.has(batchId)) {
          product.batchMap.set(batchId, {
            id: batchId,
            batchName: row.batchName || "",
            dateCreated: row.batchDateCreated,
            testingFiles: [],
          });
        }

        if (row.coa) {
          product.batchMap.get(batchId).testingFiles.push({
            id: coaFileId,
            fileName: row.coa,
            fileUrl: `https://stashhouseimages.blob.core.windows.net/productbatchfiles/${row.coa}`,
            dateCreated: row.batchDateCreated,
          });
        }
      });

      // Get brand info from vwLabReportBrands
      const allBrands = await this.getLabReportBrands();
      const brandLookup = new Map(allBrands.map((b) => [b.id, b]));

      // Convert to final structure
      const brands = Array.from(brandMap.values())
        .map((brand) => {
          const brandInfo = brandLookup.get(brand.id) || {
            id: brand.id,
            name: "Unknown Brand",
            slug: "",
            imageUrl: null,
          };

          const products = Array.from(brand.productMap.values()).map(
            (product) => {
              const batches = Array.from(product.batchMap.values());
              return {
                id: product.id,
                name: product.name,
                flavor: product.flavor,
                batches: batches,
                batchCount: batches.length,
              };
            }
          );

          return {
            ...brandInfo,
            products: products,
            productCount: products.length,
            batchCount: products.reduce((sum, p) => sum + p.batchCount, 0),
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        brands: brands,
        totalProducts: brands.reduce((sum, b) => sum + b.productCount, 0),
        totalBatches: brands.reduce((sum, b) => sum + b.batchCount, 0),
      };
    } catch (error) {
      console.error("Error in productService.searchLabReports:", error);
      throw error;
    }
  }

  /**
   * Get lab reports - returns brands with counts
   * Products are loaded on-demand when brand is expanded
   */
  async getLabReports(options = {}) {
    try {
      const { brandId, search } = options;

      // If search term provided, use search method
      if (search && search.trim().length >= 2) {
        return this.searchLabReports(search.trim());
      }

      // If brandId specified, get products for that brand
      if (brandId) {
        const products = await this.getLabReportProductsByBrand(brandId);
        const brands = await this.getLabReportBrands();
        const selectedBrand = brands.find((b) => b.id === brandId);

        return {
          brands: selectedBrand
            ? [
                {
                  ...selectedBrand,
                  products: products,
                },
              ]
            : [],
          totalProducts: products.length,
          totalBatches: products.reduce((sum, p) => sum + p.batchCount, 0),
        };
      }

      // Otherwise just return brands with counts (no products loaded yet)
      const brands = await this.getLabReportBrands();

      return {
        brands: brands.map((b) => ({ ...b, products: [] })),
        totalProducts: brands.reduce((sum, b) => sum + b.productCount, 0),
        totalBatches: brands.reduce((sum, b) => sum + b.batchCount, 0),
      };
    } catch (error) {
      console.error("Error in productService.getLabReports:", error);
      throw error;
    }
  }
}

// Export singleton instance
export default new ProductService();
